import React from 'react';
import { styled } from '@compiled/react';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { DeploymentIcons } from '@atlassian/jira-software-deployments-icons-common/src/ui/index.tsx';
import {
	type DeploymentState as DeploymentIconState,
	DeploymentStateOptions as DeploymentIconStateOptions,
} from '@atlassian/jira-software-deployments-icons-common/src/ui/types.tsx';
import {
	type Deployment,
	DeploymentEnvironmentTypeOptions,
	type DeploymentEnvironmentType,
	DeploymentStateOptions,
	type DeploymentState,
} from '../../types';
import { getColorForDeploymentState, getMostMeaningfulDeployment } from '../../utils/deployments';

export const getDeploymentIconState = (
	state: DeploymentState,
	environmentType: DeploymentEnvironmentType,
): DeploymentIconState =>
	state === DeploymentStateOptions.SUCCESSFUL &&
	environmentType !== DeploymentEnvironmentTypeOptions.PRODUCTION
		? DeploymentIconStateOptions.SUCCESSFULNONPRODUCTION
		: state;

type Props = {
	hasBorder: boolean;
	deployments: Deployment[];
	showMultipleDeploymentsIcon: boolean;
};

const DeploymentIcon = ({ deployments, showMultipleDeploymentsIcon, hasBorder }: Props) => {
	const deployment = getMostMeaningfulDeployment(deployments);
	const color = getColorForDeploymentState(deployment.state);
	const isMulti = showMultipleDeploymentsIcon && deployments.length > 1;
	const hasMoreThanNineDeployments = deployments.length > 9;
	const shouldShowIcon = !(isMulti && hasMoreThanNineDeployments);
	const deploymentState = getDeploymentIconState(deployment.state, deployment.environment.type);

	return isMulti ? (
		<Wrapper
			hasBorder={hasBorder}
			color={color}
			singleChild={hasMoreThanNineDeployments}
			isMulti={isMulti}
		>
			{shouldShowIcon && <DeploymentIcons deploymentState={deploymentState} size="small" />}
			<Count
				shouldShowIcon={shouldShowIcon}
				data-testid="software-deployments.common.ui.deployment-icon.deployment-icon-count"
			>
				{hasMoreThanNineDeployments ? '9+' : deployments.length}
			</Count>
		</Wrapper>
	) : (
		<DeploymentIcons deploymentState={deploymentState} size="small" />
	);
};

interface WrapperProps {
	color?: string;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	singleChild?: any;
	isMulti?: boolean;
	hasBorder?: boolean;
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div<WrapperProps>({
	display: 'flex',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: (props: WrapperProps) => (props.color ? props.color : 'unset'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	justifyContent: (props: WrapperProps) => (props.singleChild ? 'center' : 'space-between'),
	alignItems: 'center',
	font: token('font.body.small'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.inverse', colors.N0),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	width: (props: WrapperProps) => (props.isMulti ? '28px' : '16px'),
	height: '16px',
	borderRadius: '14px',
	/* we add a border to preserve the gap between line and indicator */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	border: (props: WrapperProps) => (props.hasBorder ? '2px solid' : 'none'),
	borderColor: 'var(--row-background-color)',
});

interface CountProps {
	shouldShowIcon: boolean;
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Count = styled.span<CountProps>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	paddingRight: ({ shouldShowIcon }: CountProps) =>
		shouldShowIcon ? token('space.050', '4px') : 0,
});

export default DeploymentIcon;
